import Cookies from 'cookies-ts';
 
declare var jQuery:any;
declare var FullCalendar:any;
declare var adminSettings:any;
declare var product_page_localize:any;
const MOBILE_MEDIA_QUERY = '(max-width: 575.98px)';
const COMPACT_CALENDAR_WIDTH = 720;
const COMPACT_CALENDAR_HEIGHT = 800;
const MOBILE_LIST_EVENT_LIMIT = 8;
 
export class MainClass {
    public calendar:any;
    public calendarEl:any;
    public calendarInitialized: boolean = false;
    private searchDebounceTimer: number | null = null;
    private searchOutsideHandler: ((e: Event) => void) | null = null;
    private mobileMediaQuery: MediaQueryList | null = null;
    private mobileMediaHandler: ((e: MediaQueryListEvent) => void) | null = null;
    private calendarLayoutObserver: ResizeObserver | null = null;
    private calendar_layout_width: number | null = null;
    private expanded_day_date: string | null = null;
    private view_select_outside_handler: ((e: Event) => void) | null = null;
    private popup_outside_handler: ((e: Event) => void) | null = null;
    private windowResizeHandler: (() => void) | null = null;
    private resizeDebounceTimer: number | null = null;
    private timezone_tooltip: HTMLElement | null = null;
    private timezone_tooltip_keydown_handler: ((event: KeyboardEvent) => void) | null = null;
    private currentFilter: 'all' | 'my' = 'all';
    private currentSearch: string = '';
    private current_event_shape: 'allday' | 'timed' = 'allday';
    private overflow_cleanup_token: number = 0;
    private list_overflow_cleanup_token: number = 0;
    private list_overflow_expanded: boolean = false;
 
    constructor() {
        this.calendarEl = document.getElementById('calendar');
        this.timezone_tooltip_keydown_handler = (event: KeyboardEvent) => {
          if (event.key === 'Escape') {
            this.hide_timezone_tooltip();
          }
        };
        document.addEventListener('keydown', this.timezone_tooltip_keydown_handler);
    }
 
    private i18n(key: string, fallback: string): string {
        const strings = (typeof adminSettings !== 'undefined' && adminSettings && adminSettings.i18n) || {};
        return (strings[key] as string) || fallback;
    }
 
    private isMobileViewport(): boolean {
      if (typeof window === 'undefined' || !window.matchMedia) return false;
      return window.matchMedia(MOBILE_MEDIA_QUERY).matches;
    }

    private isLoggedIn(): boolean {
        return typeof adminSettings !== 'undefined' && !!adminSettings.is_logged_in;
    }

    private getCompactCalendarHeight(): number | null {
      const container = this.calendarEl?.closest('.fullcalendar_content') as HTMLElement | null;
      const calendar_width = container?.clientWidth || window.innerWidth;
      return calendar_width <= COMPACT_CALENDAR_WIDTH ? COMPACT_CALENDAR_HEIGHT : null;
    }

    private getEventShapeForView(view_type: string): 'allday' | 'timed' {
      return (view_type === 'timeGridWeek' || view_type === 'timeGridDay') ? 'timed' : 'allday';
    }
 
    private applyCalendarHeightForView(view_type?: string) {
      if (!this.calendar) return;

      const view = view_type || this.calendar.view?.type || 'dayGridMonth';
      const is_month = view === 'dayGridMonth';
      // List is a plain stacked list, not a fixed viewport region like Week/Day's
      // hour grid — size it to its (capped) content instead of a fixed compact
      // box, or the event-count cap above just leaves blank space in an
      // already-fixed-height box instead of actually shortening the page.
      const is_list = view === 'listWeek';

      if (is_month || is_list) {
        this.calendar.setOption('height', 'auto');
        this.calendar.setOption('aspectRatio', undefined);
        this.calendar.setOption('expandRows', false);
      } else {
        this.calendar.setOption('height', this.getCompactCalendarHeight());
        this.calendar.setOption('aspectRatio', 2);
        this.calendar.setOption('expandRows', true);
      }

      this.calendar.updateSize();
    }

    private buildHeaderToolbarConfig(isMobile: boolean) {
      return {
        left: 'today prev,next',
        center: 'title',
        right: '',
      };
    }
 
    public init() {
      this.initCalendar();
      this.calendar.render();
    }
 
    public getValidLanguage(lang?: string): string | undefined {
      return lang && ['en_US', 'fr_FR'].includes(lang) ? lang : undefined;
    }
 
    public initCalendar() {
      const cookie = new Cookies();
      const cookieValue: string | undefined = cookie.get('wp_lang');
      const defaultLanguage = cookieValue ? cookieValue.split('_')[0] : 'en';
      
      this.calendar = new FullCalendar.Calendar(this.calendarEl, {
        locale: defaultLanguage,
        buttonText: adminSettings.buttonText || {},
        showNonCurrentDates: false,
        navLinks: true,
        navLinkDayClick: this.handleNavLinkDayClick.bind(this),
        editable: false,
        dayMaxEvents: 3,
        slotEventOverlap: false,
        height: 'auto',
        expandRows: false,
        displayEventTime: false,
        events: {
            url: adminSettings.admin_url,
            extraParams: () => ({
                action: 'get_events',
                nonce: adminSettings.nonce,
                filter: this.currentFilter,
                search: this.currentSearch,
                view: this.calendar?.view?.type || 'dayGridMonth',
            }),
        },
        eventDidMount: this.eventDidMount.bind(this),
        eventsSet: this.onEventsSet.bind(this),
        eventClick: this.eventClick.bind(this),
        viewDidMount: (viewInfo: any) => {
          this.onViewDidMount(viewInfo);
          this.calendarInitialized = true;
        },
        datesSet: this.onDatesSet.bind(this),
        loading: this.toggleLoading.bind(this),
        moreLinkClick: this.handleMoreLinkClick.bind(this),
        moreLinkText: (num: number) => this.isMobileViewport() ? `+${num}` : `+${num} more`,
        views: {
          dayGridMonth: {
            eventDisplay: 'block',
            expandRows: false,
          },
        },
      });
 
       // Render the calendar with the initial locale and headerToolbar
      this.calendar.render();
 
      // Update the locale and headerToolbar whenever needed
      this.updateCalendarOptions(defaultLanguage);
 
      // Track viewport so the toolbar config swaps automatically on resize.
      this.bindMobileMediaQuery();
      this.bindCalendarLayoutScale();

      // Recompute grid dimensions on any resize so the calendar never keeps
      // stale width/height from a previous viewport (122811).
      this.bindWindowResize();
    }

    private handleNavLinkDayClick(date: Date, js_event: MouseEvent) {
      js_event.preventDefault();
      this.expanded_day_date = null;
      this.calendar.changeView('timeGridDay', date);
    }

    private handleMoreLinkClick(info: any) {
      if (info.view.type !== 'timeGridDay' || !info.allDay) return 'popover';

      this.expanded_day_date = this.calendar.view.currentStart.toISOString().slice(0, 10);
      this.calendar.setOption('dayMaxEvents', false);
      this.calendar.setOption('eventMaxStack', null);
      return true;
    }

    private resetDayOverflow() {
      if (!this.expanded_day_date) return;

      this.expanded_day_date = null;
      this.calendar.setOption('dayMaxEvents', 3);
      this.calendar.setOption('eventMaxStack', null);
    }

    private updateCalendarOptions(locale: string) {
      // Set the new locale
      this.calendar.setOption('locale', locale);
 
      // Set the headerToolbar based on the current viewport.
      this.applyHeaderToolbarForViewport();
 
      // Refetch events (if needed)
      this.calendar.refetchEvents();
 
      // Mount the toolbar search after FullCalendar has rendered the toolbar.
      this.mountToolbarSearch();
      this.mountToolbarViewSelect();
      this.mountToolbarFilter();
    }

    private applyHeaderToolbarForViewport() {
      if (!this.calendar) return;
      const config = this.buildHeaderToolbarConfig(false);
      this.calendar.setOption('headerToolbar', config);
      this.applyCalendarHeightForView();
      this.mountToolbarSearch();
      this.mountToolbarViewSelect();
      this.mountToolbarFilter();
    }

    private bindWindowResize() {
      if (typeof window === 'undefined') return;
      if (this.windowResizeHandler) return;

      this.windowResizeHandler = () => {
        if (this.resizeDebounceTimer !== null) {
          window.clearTimeout(this.resizeDebounceTimer);
        }
        this.resizeDebounceTimer = window.setTimeout(() => {
          this.resizeDebounceTimer = null;
          if (this.calendar) this.calendar.updateSize();
        }, 150);
      };

      window.addEventListener('resize', this.windowResizeHandler);
    }

    private bindMobileMediaQuery() {
      if (typeof window === 'undefined' || !window.matchMedia) return;
      if (this.mobileMediaQuery && this.mobileMediaHandler) return;
 
      this.mobileMediaQuery = window.matchMedia(MOBILE_MEDIA_QUERY);
      this.mobileMediaHandler = (_e: MediaQueryListEvent) => {
        this.applyHeaderToolbarForViewport();
      };
 
      if (typeof this.mobileMediaQuery.addEventListener === 'function') {
        this.mobileMediaQuery.addEventListener('change', this.mobileMediaHandler);
      } else if (typeof (this.mobileMediaQuery as any).addListener === 'function') {
        // Safari < 14 fallback
        (this.mobileMediaQuery as any).addListener(this.mobileMediaHandler);
      }
    }

    private bindCalendarLayoutScale() {
      const container = this.calendarEl?.closest('.fullcalendar_content') as HTMLElement | null;
      if (!container) return;

      const updateScale = () => {
        const calendar_width = container.clientWidth;
        const scale = Math.min(1, calendar_width / 360);
        const scaleValue = scale.toString();
        if (container.style.getPropertyValue('--vm-calendar-scale') !== scaleValue) {
          container.style.setProperty('--vm-calendar-scale', scaleValue);
        }
        if (this.calendar_layout_width === calendar_width) return;
        this.calendar_layout_width = calendar_width;
        this.applyCalendarHeightForView();
        window.requestAnimationFrame(() => this.scheduleMonthOverflowCleanup());
      };

      updateScale();
      this.calendarLayoutObserver?.disconnect();
      this.calendarLayoutObserver = new ResizeObserver(updateScale);
      this.calendarLayoutObserver.observe(container);
    }
 
    private mountToolbarSearch() {
      if (!this.calendarEl) return;
 
      const toolbar = this.calendarEl.querySelector('.fc-header-toolbar') as HTMLElement | null;
      if (!toolbar) return;
 
      // Idempotent — if already mounted inside the current toolbar, bail out.
      if (toolbar.querySelector('.vm-cal-search')) return;
 
      const chunks = toolbar.querySelectorAll('.fc-toolbar-chunk');
      const host: HTMLElement = (chunks.length > 0
        ? chunks[chunks.length - 1]
        : toolbar) as HTMLElement;
 
      const wrapper = document.createElement('div');
      wrapper.className = 'vm-cal-search';
 
      const toggle = document.createElement('button');
      toggle.type = 'button';
      toggle.className = 'vm-cal-search__toggle';
      toggle.setAttribute('aria-label', this.i18n('search_toggle_aria', 'Toggle event search'));
      toggle.setAttribute('aria-expanded', 'false');
      toggle.innerHTML =
        '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">' +
        '<path d="M15.5 14h-.79l-.28-.27a6.5 6.5 0 1 0-.7.7l.27.28v.79l5 5 1.49-1.49-5-5zm-6 0a4.5 4.5 0 1 1 0-9 4.5 4.5 0 0 1 0 9z"/>' +
        '</svg>';
 
      const panel = document.createElement('div');
      panel.className = 'vm-cal-search__panel';
 
      const input = document.createElement('input');
      input.type = 'search';
      input.className = 'vm-cal-search__input';
      input.placeholder = this.i18n('search_placeholder', 'Search events');
      input.setAttribute('aria-label', this.i18n('search_aria_label', 'Search events'));
 
      panel.appendChild(input);
      wrapper.appendChild(toggle);
      wrapper.appendChild(panel);
      host.prepend(wrapper);
 
      input.addEventListener('input', (e: Event) => {
        const value = (e.target as HTMLInputElement).value || '';
        this.scheduleSearch(value);
      });
 
      toggle.addEventListener('click', (e: Event) => {
        e.stopPropagation();
        const isOpen = wrapper.classList.toggle('is-open');
        toggle.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
        if (isOpen) {
          input.focus();
        }
      });
 
      // Prevent clicks inside the panel from triggering the outside-click close.
      panel.addEventListener('click', (e: Event) => {
        e.stopPropagation();
      });
 
      input.addEventListener('keydown', (e: KeyboardEvent) => {
        if (e.key === 'Escape') {
          wrapper.classList.remove('is-open');
          toggle.setAttribute('aria-expanded', 'false');
          toggle.focus();
        }
      });
 
      // Outside-click closes the dropdown (only one listener at a time).
      if (this.searchOutsideHandler) {
        document.removeEventListener('click', this.searchOutsideHandler);
      }
      this.searchOutsideHandler = (evt: Event) => {
        if (!wrapper.classList.contains('is-open')) return;
        const target = evt.target as Node;
        if (!wrapper.contains(target)) {
          wrapper.classList.remove('is-open');
          toggle.setAttribute('aria-expanded', 'false');
        }
      };
      document.addEventListener('click', this.searchOutsideHandler);
    }
 
    private mountToolbarViewSelect() {
      if (!this.calendarEl) return;
 
      const toolbar = this.calendarEl.querySelector('.fc-header-toolbar') as HTMLElement | null;
      if (!toolbar) return;
 
      const existing = toolbar.querySelector('.vm-cal-view-select');
      if (existing) {
        const current_view = this.calendar.view?.type || 'dayGridMonth';
        const current_option = toolbar.querySelector(`.vm-cal-view-option[data-value="${current_view}"]`);
        (existing as HTMLButtonElement).textContent = current_option?.textContent || 'Month';
        toolbar.querySelectorAll('.vm-cal-view-option').forEach((option) => {
          option.setAttribute('aria-selected', option.getAttribute('data-value') === current_view ? 'true' : 'false');
        });
        return;
      }
 
      const chunks = toolbar.querySelectorAll('.fc-toolbar-chunk');
      const host: HTMLElement = (chunks.length > 0
        ? chunks[chunks.length - 1]
        : toolbar) as HTMLElement;
      const control = document.createElement('div');
      control.className = 'vm-cal-view-control';
 
      const select = document.createElement('button');
      select.type = 'button';
      select.className = 'vm-cal-view-select';
      select.setAttribute('aria-haspopup', 'listbox');
      select.setAttribute('aria-expanded', 'false');
      select.setAttribute('aria-label', this.i18n('view_select_aria', 'Calendar view'));
 
      const menu = document.createElement('div');
      menu.className = 'vm-cal-view-menu';
      menu.setAttribute('role', 'listbox');
      menu.setAttribute('aria-label', this.i18n('view_select_aria', 'Calendar view'));
 
      const view_options = [
        ['dayGridMonth', 'Month'],
        ['timeGridWeek', 'Week'],
        ['timeGridDay', 'Day'],
        ['listWeek', 'List'],
      ];
      view_options.forEach(([value, label]) => {
        const option = document.createElement('button');
        option.type = 'button';
        option.className = 'vm-cal-view-option';
        option.setAttribute('role', 'option');
        option.setAttribute('data-value', value);
        option.setAttribute('aria-selected', 'false');
        option.textContent = label;
        option.addEventListener('click', (event: Event) => {
          event.stopPropagation();
          // Re-issuing changeView() for the already-active view re-renders it
          // without correctly reapplying the dayMaxEvents "+more" truncation,
          // expanding every day cell to show all events.
          if (this.calendar.view?.type !== value) {
            this.calendar.changeView(value);
          }
          control.classList.remove('is-open');
          select.setAttribute('aria-expanded', 'false');
        });
        menu.appendChild(option);
      });
 
      const current_view = this.calendar.view?.type || 'dayGridMonth';
      const current_option = menu.querySelector(`.vm-cal-view-option[data-value="${current_view}"]`);
      select.textContent = current_option?.textContent || 'Month';
      current_option?.setAttribute('aria-selected', 'true');
 
      select.addEventListener('click', (event: Event) => {
        event.stopPropagation();
        const is_open = control.classList.toggle('is-open');
        select.setAttribute('aria-expanded', is_open ? 'true' : 'false');
      });
 
      select.addEventListener('keydown', (event: KeyboardEvent) => {
        if (event.key === 'ArrowDown' || event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          control.classList.add('is-open');
          select.setAttribute('aria-expanded', 'true');
          (menu.querySelector('.vm-cal-view-option') as HTMLButtonElement | null)?.focus();
        } else if (event.key === 'Escape') {
          control.classList.remove('is-open');
          select.setAttribute('aria-expanded', 'false');
        }
      });
 
      menu.addEventListener('click', (event: Event) => {
        event.stopPropagation();
      });
 
      if (this.view_select_outside_handler) {
        document.removeEventListener('click', this.view_select_outside_handler);
      }
      this.view_select_outside_handler = (event: Event) => {
        if (!control.contains(event.target as Node)) {
          control.classList.remove('is-open');
          select.setAttribute('aria-expanded', 'false');
        }
      };
      document.addEventListener('click', this.view_select_outside_handler);
 
      control.appendChild(select);
      control.appendChild(menu);
      host.appendChild(control);
    }
 
    private scheduleSearch(value: string) {
      if (this.searchDebounceTimer !== null) {
        window.clearTimeout(this.searchDebounceTimer);
      }
      this.searchDebounceTimer = window.setTimeout(() => {
        this.searchDebounceTimer = null;
        this.runSearch(value);
      }, 400);
    }
 
    private runSearch(value: string) {
      if (!this.calendar) return;
      this.currentSearch = value;
      this.calendar.refetchEvents();
    }
    
    public render() {
        this.calendar.render();
    }

    private get_timezone_tooltip(): HTMLElement {
      if (this.timezone_tooltip) return this.timezone_tooltip;

      const tooltip = document.createElement('div');
      tooltip.id = 'vm-calendar-timezone-tooltip';
      tooltip.className = 'vm-calendar-timezone-tooltip';
      tooltip.setAttribute('role', 'tooltip');
      tooltip.hidden = true;
      document.body.appendChild(tooltip);
      this.timezone_tooltip = tooltip;

      return tooltip;
    }

    private show_timezone_tooltip(button: HTMLButtonElement): void {
      const tooltip_text = button.dataset.tooltip;
      if (!tooltip_text) return;

      const tooltip = this.get_timezone_tooltip();
      tooltip.textContent = tooltip_text;
      tooltip.hidden = false;
      tooltip.style.left = '0';
      tooltip.style.top = '0';

      const button_rect = button.getBoundingClientRect();
      const margin = 12;
      const gap = 8;
      const tooltip_max_width = Math.max(0, window.innerWidth - margin * 2);
      const tooltip_max_height = Math.max(0, window.innerHeight - margin * 2);
      tooltip.style.maxWidth = `${tooltip_max_width}px`;
      tooltip.style.maxHeight = `${tooltip_max_height}px`;
      tooltip.style.overflowY = 'hidden';
      const has_vertical_overflow = tooltip.scrollHeight > tooltip.clientHeight + 5;
      tooltip.style.overflowY = has_vertical_overflow ? 'auto' : 'hidden';
      const tooltip_rect = tooltip.getBoundingClientRect();
      const show_above = button_rect.top - tooltip_rect.height - gap >= margin;
      const initial_top = show_above
        ? button_rect.top - tooltip_rect.height - gap
        : button_rect.bottom + gap;
      const maximum_top = window.innerHeight - tooltip_rect.height - margin;
      const top = Math.max(margin, Math.min(initial_top, maximum_top));
      const left = Math.max(
        margin,
        Math.min(
          button_rect.left + button_rect.width / 2 - tooltip_rect.width / 2,
          window.innerWidth - tooltip_rect.width - margin
        )
      );

      tooltip.dataset.placement = show_above ? 'above' : 'below';
      tooltip.style.left = `${left}px`;
      tooltip.style.top = `${top}px`;
      button.setAttribute('aria-describedby', tooltip.id);
    }

    private hide_timezone_tooltip(): void {
      if (!this.timezone_tooltip) return;

      this.timezone_tooltip.hidden = true;
      document.querySelectorAll('.vm-tz-info[aria-describedby="vm-calendar-timezone-tooltip"]').forEach((button) => {
        button.removeAttribute('aria-describedby');
      });
    }

    private bind_timezone_tooltips(calendar_popover: HTMLElement): void {
      calendar_popover.querySelectorAll<HTMLButtonElement>('.vm-tz-info[data-tooltip]').forEach((button) => {
        button.addEventListener('mouseenter', () => this.show_timezone_tooltip(button));
        button.addEventListener('mouseleave', () => this.hide_timezone_tooltip());
        button.addEventListener('focus', () => this.show_timezone_tooltip(button));
        button.addEventListener('blur', () => this.hide_timezone_tooltip());
        button.addEventListener('click', (event) => {
          event.preventDefault();
          this.show_timezone_tooltip(button);
        });
      });
    }
 
    public eventClick(eventClickInfo: any) {
      // Prevent the default behavior of the click event
      eventClickInfo.jsEvent.preventDefault();
  
      // Call the openModal function with the eventClickInfo parameter
      this.openModal(eventClickInfo);
  
      if (this.popup_outside_handler) {
          document.removeEventListener('click', this.popup_outside_handler);
      }
 
      // Define a function to handle clicks outside the modal
      this.popup_outside_handler = (e: Event) => {
          // Get the modal ID
          const modal = document.getElementById('calendar-pop-over');
  
          // Check if the click event occurred inside the modal and not on the event element
          if (modal && !modal.contains(e.target as Node) && !eventClickInfo.el.contains(e.target as Node)) {
              // Call the hidePopOver function to close the modal
              this.hidePopOver(eventClickInfo);
  
              // Remove the click event listener to prevent further checks
              document.removeEventListener('click', this.popup_outside_handler);
              this.popup_outside_handler = null;
          }
      };
  
      // Add the click event listener to the document to handle clicks outside the modal
      document.addEventListener('click', this.popup_outside_handler);
    }
  
    public openModal(eventClickInfo: any) {
      const elemRect = eventClickInfo.el.getBoundingClientRect();
      const calendarPopOver = document.getElementById('calendar-pop-over');
      const was_open = calendarPopOver.classList.contains('is-open');
 
      jQuery(calendarPopOver.querySelector('.popover-title')).text(eventClickInfo.event.title);
      jQuery(calendarPopOver.querySelector('.popover-content')).html(eventClickInfo.event.extendedProps.description);
      this.bind_timezone_tooltips(calendarPopOver);
      const event_color = eventClickInfo.event.backgroundColor
        || eventClickInfo.event.borderColor
        || window.getComputedStyle(eventClickInfo.el).backgroundColor
        || '#1a73e8';
      calendarPopOver.style.setProperty('--vm-calendar-event-color', event_color);
 
      if (!was_open) {
        jQuery(calendarPopOver).css({top: '0px', left: '0px'}).show();
      }
 
      const popoverRect = calendarPopOver.getBoundingClientRect();
      const viewportMargin = 12;
      const eventGap = 15;
      const can_place_left = elemRect.width < popoverRect.width
        && elemRect.left - popoverRect.width - eventGap >= viewportMargin;
      const availableAbove = elemRect.top - viewportMargin;
      const availableBelow = window.innerHeight - elemRect.bottom - viewportMargin;
      const shouldPlaceAbove = eventClickInfo.jsEvent.y / window.innerHeight > 0.60
        || (availableBelow < popoverRect.height && availableAbove > availableBelow);
      let initTop = can_place_left
        ? elemRect.top
        : shouldPlaceAbove
          ? elemRect.top - popoverRect.height - eventGap
          : elemRect.bottom + eventGap;
      let initleft = can_place_left
        ? elemRect.left - popoverRect.width - eventGap
        : elemRect.left + (elemRect.width - popoverRect.width) / 2;
 
      initTop = Math.max(
        viewportMargin,
        Math.min(initTop, window.innerHeight - popoverRect.height - viewportMargin)
      );
      initleft = Math.max(
        viewportMargin,
        Math.min(initleft, window.innerWidth - popoverRect.width - viewportMargin)
      );
 
      if (shouldPlaceAbove) {
        jQuery(calendarPopOver).removeClass('bottom').addClass('top');
      } else {
        jQuery(calendarPopOver).addClass('bottom').removeClass('top');
      }
 
      const enter_from_right = can_place_left || initleft <= viewportMargin + 4;
      calendarPopOver.style.setProperty('--vm-calendar-popup-enter-x', enter_from_right ? '4px' : '-4px');
      jQuery(calendarPopOver).css('top', initTop + 'px');
      jQuery(calendarPopOver).css('left', initleft + 'px');
      jQuery(calendarPopOver).show();
      if (!was_open) {
        window.requestAnimationFrame(() => calendarPopOver.classList.add('is-open'));
      }
 
      const close_button = calendarPopOver.querySelector('.close-modal');
      if (close_button) {
        close_button.addEventListener('click', () => this.hidePopOver(eventClickInfo), {once: true});
      }
    }
 
    public destroy() {
        this.hide_timezone_tooltip();
        this.timezone_tooltip?.remove();
        this.timezone_tooltip = null;
        if (this.timezone_tooltip_keydown_handler) {
          document.removeEventListener('keydown', this.timezone_tooltip_keydown_handler);
          this.timezone_tooltip_keydown_handler = null;
        }
        this.calendar.destroy();
    }
 
    public currentDateStart() {
        let startUTCDate =  this.calendar.view.currentStart.toUTCString();
        let startDate = new Date(startUTCDate);
 
        return startDate.toISOString();
    }
 
    public currentDateEnd() {
        let endUTCDate =  this.calendar.view.currentEnd.toUTCString();
        let endDate = new Date(endUTCDate);
 
        return endDate.toISOString();
    }
 
    public hidePopOver(eventClickInfo:any) {
        const calendarPopOver = document.getElementById('calendar-pop-over');
        if (!calendarPopOver) return;

        this.hide_timezone_tooltip();
        calendarPopOver.classList.remove('is-open');
        window.setTimeout(() => {
          if (!calendarPopOver.classList.contains('is-open')) {
            jQuery(calendarPopOver).hide();
          }
        }, 160);
    }
 
    public get_data(searchEvent: any, start: any, end: any) {
      // Ensure that the calendar is properly initialized
      if (!this.calendarInitialized) {
          this.initCalendar();
      }
  
      // Set the initial date before loading events
      this.calendar.setOption('initialDate', start);
  
      // Load events from the server
      this.calendar.setOption('events', {
          url: product_page_localize.ajax_url,
          extraParams: {
              action: 'get_events',
              search: searchEvent,
              start: start,
              end: end,
              nonce: adminSettings.nonce,
          },
      });
  
      // Render the calendar
      this.calendar.render();
    }
 
    public getOffset(el:any) {
        const rect = el.getBoundingClientRect();
        return {
            left: rect.left + window.scrollX,
            top: rect.top + window.scrollY
        };
    }

    private mountToolbarFilter() {
      if (!this.calendarEl) return;
      if (!this.isLoggedIn()) return;

      const toolbar = this.calendarEl.querySelector('.fc-header-toolbar') as HTMLElement | null;
      if (!toolbar) return;

      if (toolbar.querySelector('.vm-cal-filter')) return;

      const chunks = toolbar.querySelectorAll('.fc-toolbar-chunk');
      const host: HTMLElement = (chunks.length > 0 ? chunks[0] : toolbar) as HTMLElement;

      const group = document.createElement('div');
      group.className = 'vm-cal-filter';
      group.setAttribute('role', 'radiogroup');
      group.setAttribute('aria-label', this.i18n('filter_aria_label', 'Filter events'));

      const options: Array<{ value: 'all' | 'my'; label: string }> = [
        { value: 'all', label: this.i18n('all_events', 'All Events') },
        { value: 'my',  label: this.i18n('my_events',  'My Events') },
      ];

      const buttons: HTMLButtonElement[] = [];
      options.forEach((opt) => {
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'vm-cal-filter__option';
        btn.setAttribute('role', 'radio');
        btn.setAttribute('data-value', opt.value);
        btn.textContent = opt.label;
        const isChecked = this.currentFilter === opt.value;
        btn.setAttribute('aria-checked', isChecked ? 'true' : 'false');
        btn.setAttribute('tabindex', isChecked ? '0' : '-1');
        if (isChecked) btn.classList.add('is-active');

        btn.addEventListener('click', () => this.selectFilter(opt.value, buttons));
        btn.addEventListener('keydown', (e: KeyboardEvent) => {
          if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
            e.preventDefault();
            const idx = buttons.indexOf(btn);
            const next = buttons[(idx + 1) % buttons.length];
            next.focus();
            this.selectFilter(next.getAttribute('data-value') as 'all' | 'my', buttons);
          } else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
            e.preventDefault();
            const idx = buttons.indexOf(btn);
            const prev = buttons[(idx - 1 + buttons.length) % buttons.length];
            prev.focus();
            this.selectFilter(prev.getAttribute('data-value') as 'all' | 'my', buttons);
          }
        });

        buttons.push(btn);
        group.appendChild(btn);
      });

      host.appendChild(group);
    }

    private selectFilter(value: 'all' | 'my', buttons: HTMLButtonElement[]) {
      if (!this.calendar) return;
      if (this.currentFilter === value) return;
      this.currentFilter = value;

      buttons.forEach((b) => {
        const isActive = b.getAttribute('data-value') === value;
        b.setAttribute('aria-checked', isActive ? 'true' : 'false');
        b.setAttribute('tabindex', isActive ? '0' : '-1');
        b.classList.toggle('is-active', isActive);
      });

      // Close any open Details popup when switching between All/My events,
      // mirroring the view-switch behavior in onDatesSet().
      this.hidePopOver(null);

      this.calendar.refetchEvents();
    }

    public onViewDidMount(viewInfo: any) {
      this.mountToolbarSearch();
      this.mountToolbarViewSelect();
      this.mountToolbarFilter();
      if (viewInfo.view.type === 'dayGridMonth') {
        this.scheduleMonthOverflowCleanup();
      }
      if (viewInfo.view.type === 'listWeek') {
        this.scheduleListOverflowCleanup();
      }
    }

    public onEventsSet() {
      // Fires after events are (re)rendered — including filter/search refetch,
      // where onViewDidMount does NOT run. Re-syncing here keeps overflow events
      // from reappearing over the "+N more" link after a My<->All toggle (122539).
      this.scheduleMonthOverflowCleanup();
      this.scheduleListOverflowCleanup();
    }
 
    /**
     * Re-sync overflow event-harness visibility in month view. FullCalendar hides
     * overflow harnesses (visibility:hidden) behind the "+N more" link, but our
     * hidden harnesses must be display:none'd to stay out of the layout. Must run
     * after EVERY event render (mount, navigation AND refetch); running only once
     * left overflow events overlapping after a filter toggle (122539).
     */
    private scheduleMonthOverflowCleanup() {
      if (!this.calendar || this.calendar.view?.type !== 'dayGridMonth') return;

      // Cancel any still-running cleanup loop from an earlier trigger (e.g. a
      // prior tick in a fast resize sequence) before starting a new one — two
      // overlapping loops race on the same harnesses and the older one can
      // restore (display:'') an event the newer/settled layout wants hidden
      // behind "+N more", inflating the day cell past dayMaxEvents.
      const token = ++this.overflow_cleanup_token;
      let frames = 0;
      const tick = () => {
        if (token !== this.overflow_cleanup_token) return;
        const elements = document.getElementsByClassName('fc-daygrid-event-harness');
        if (elements.length > 0) {
          this.hideDivsWithHiddenVisibility(elements);
        }
        // Re-run across a few frames to catch FullCalendar's final layout pass.
        if (frames++ < 5) requestAnimationFrame(tick);
      };
      requestAnimationFrame(tick);
    }
 
    /**
     * On mobile, cap the List view to MOBILE_LIST_EVENT_LIMIT events with a
     * "Show N more" row, instead of letting the page grow to fit every event
     * (avoids both the unbounded-page-length and the nested-scroll extremes).
     */
    private scheduleListOverflowCleanup() {
      if (!this.calendar || this.calendar.view?.type !== 'listWeek') return;

      const token = ++this.list_overflow_cleanup_token;
      let frames = 0;
      const tick = () => {
        if (token !== this.list_overflow_cleanup_token) return;
        this.applyListOverflowLimit();
        if (frames++ < 5) requestAnimationFrame(tick);
      };
      requestAnimationFrame(tick);
    }

    private applyListOverflowLimit() {
      const table = this.calendarEl?.querySelector('.fc-list-table');
      if (!table) return;

      const existing_toggle = table.querySelector('.vm-cal-list-more-row');
      if (existing_toggle) existing_toggle.remove();

      const rows = [...table.querySelectorAll('tr')] as HTMLElement[];
      if (!this.isMobileViewport() || this.list_overflow_expanded) {
        rows.forEach((row) => { row.style.display = ''; });
        return;
      }

      let event_count = 0;
      let hidden_event_count = 0;
      let cutoff_row: HTMLElement | null = null;

      rows.forEach((row) => {
        if (!row.classList.contains('fc-list-event')) return;
        event_count += 1;
        if (event_count <= MOBILE_LIST_EVENT_LIMIT) {
          row.style.display = '';
        } else {
          row.style.display = 'none';
          hidden_event_count += 1;
          if (!cutoff_row) cutoff_row = row;
        }
      });

      // Hide day-header rows whose events are now entirely hidden.
      let current_day_row: HTMLElement | null = null;
      let current_day_has_visible_event = false;
      rows.forEach((row) => {
        if (row.classList.contains('fc-list-day')) {
          if (current_day_row) {
            current_day_row.style.display = current_day_has_visible_event ? '' : 'none';
          }
          current_day_row = row;
          current_day_has_visible_event = false;
        } else if (row.classList.contains('fc-list-event') && row.style.display !== 'none') {
          current_day_has_visible_event = true;
        }
      });
      if (current_day_row) {
        current_day_row.style.display = current_day_has_visible_event ? '' : 'none';
      }

      if (hidden_event_count > 0 && cutoff_row) {
        const toggle_row = document.createElement('tr');
        toggle_row.className = 'vm-cal-list-more-row';
        const cell = document.createElement('td');
        cell.colSpan = 3;
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'vm-cal-list-more-button';
        button.textContent = this.i18n('list_show_more', 'Show %d more')
          .replace('%d', String(hidden_event_count));
        button.addEventListener('click', () => {
          this.list_overflow_expanded = true;
          this.applyListOverflowLimit();
        });
        cell.appendChild(button);
        toggle_row.appendChild(cell);
        cutoff_row.parentElement?.insertBefore(toggle_row, cutoff_row);
      }
    }

    public hideDivsWithHiddenVisibility(elements: any) {
      for (let i = 0; i < elements.length; i++) {
        const element = elements[i] as HTMLElement;
        const visibility = jQuery(element).css('visibility');
        if (visibility === 'hidden' || visibility === 'none') {
          element.style.display = 'none';
        } else {
          // Restore harnesses that are no longer overflow (e.g. after a refetch
          // reuses a node we previously hid), so visible events are never dropped.
          element.style.display = '';
        }
      }
    }
  
    public onDatesSet(dateInfo: any) {
      const view_date = dateInfo.view.currentStart.toISOString().slice(0, 10);
      if (dateInfo.view.type !== 'timeGridDay' || this.expanded_day_date !== view_date) {
        this.resetDayOverflow();
      }
      this.list_overflow_expanded = false;

      const next_event_shape = this.getEventShapeForView(dateInfo.view.type);
      if (next_event_shape !== this.current_event_shape) {
        this.current_event_shape = next_event_shape;
        this.calendar.refetchEvents();
      }

      this.applyCalendarHeightForView(dateInfo.view.type);

      // Close the Details popup when switching views or navigating dates
      this.hidePopOver(null);
      this.onViewDidMount(dateInfo);
    }
 
    private eventDidMount(info: any) {
      const element = info.el;
      // Preserve FullCalendar's visibility state for events hidden behind the
      // dayMaxEvents overflow link.
      element.style.visibility = '';
    }
  
    private toggleLoading(bool: boolean) {
      const loadingElement = document.getElementById('loading');
      if (loadingElement) {
        loadingElement.style.display = bool ? 'block' : 'none';
      }
    }
}
